import { merge, Observable } from 'rxjs';
import { filter, map, mapTo, shareReplay, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { ChangeDetectorRef, Component, Input, inject, ChangeDetectionStrategy } from '@angular/core';

import { ActionCompletion, Actions, ofActionCompleted, ofActionDispatched } from '@ngxs/store';
import { ImageUploadService } from '@shared/modules/image-upload/services/image-upload.service';
import { UploadImage, UploadImageProgress } from '@shared/modules/image-upload/image-upload.actions';
import { isImageFile } from '@shared/utilities/string.utilities';
import { ImageFit } from '@shared/modules/image-upload/models/image-upload.models';

@Component({
  selector: 'image-upload-area',
  templateUrl: './image-upload-area.component.html',
  styleUrls: ['./image-upload-area.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ImageUploadAreaComponent {
  private cd = inject(ChangeDetectorRef);
  private actions = inject(Actions);
  readonly iu = inject(ImageUploadService);

  @Input()
  fit: ImageFit = 'contain';

  hasError?: boolean;

  readonly uploading$: Observable<boolean> = merge(
    this.actions.pipe(
      ofActionDispatched(UploadImage),
      filter(({ image }: UploadImage) => image === this.currentFile),
      mapTo(true),
    ),
    this.actions.pipe(
      ofActionCompleted(UploadImage),
      filter(({ action: { image } }: ActionCompletion<UploadImage>) => image === this.currentFile),
      mapTo(false),
    ),
  ).pipe(startWith(false), shareReplay({ refCount: true, bufferSize: 1 }));

  readonly progress$: Observable<number> = this.uploading$.pipe(
    filter((uploading) => uploading),
    switchMap(() =>
      this.actions.pipe(
        ofActionDispatched(UploadImageProgress),
        filter(({ image }: UploadImageProgress) => image === this.currentFile),
        map(({ progress }: UploadImageProgress) => (progress === 1 ? 0 : progress)),
        takeUntil(this.uploading$.pipe(filter((uploading) => !uploading))),
      ),
    ),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  private currentFile: File | undefined;

  getImageSrc(): string {
    return this.iu.gallery || this.hasError || this.iu.customGallery ? '' : this.iu.source;
  }

  onFileChange(file: File): void {
    this.currentFile = void 0;

    if (!file) {
      return;
    }

    this.hasError = !isImageFile(file);

    if (!this.hasError) {
      this.currentFile = file;

      this.iu.uploadImage(file).subscribe();
    }

    this.cd.markForCheck();
  }
}
